import React, { useState, useEffect } from 'react';
import {
  Search, FileSpreadsheet, Printer, Loader2, Trash2, Edit3,
  Calendar, Filter, FileText, RefreshCw, Save, X, Send,
  ChevronDown, ChevronUp
} from 'lucide-react';
import * as XLSX from 'xlsx';
import toast, { Toaster } from 'react-hot-toast';
import './ExpiryAnalysis.css';
import {
  getInsuranceData,
  deleteInsuranceData,
  updateInsuranceData,
} from "../Services/Services";
import { Pagination } from 'antd';

const ExpiryAnalysis = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [docType, setDocType] = useState('all');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobilePage, setMobilePage] = useState(1);
  const mobilePageSize = 5;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const response = await getInsuranceData();
      const rawData = response.results || response || [];
      const sortedData = Array.isArray(rawData)
        ? rawData.sort((a, b) => {
          const dateA = a.taxValidity || a.insuranceValidity || a.fitnessValidity;
          const dateB = b.taxValidity || b.insuranceValidity || b.fitnessValidity;
          return new Date(dateA) - new Date(dateB);
        })
        : [];
      setData(sortedData);
    } catch (error) {
      toast.error("Data load nahi ho paya!");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInitialData(); }, []);

  const handleRefresh = () => {
    setSearchQuery('');
    setFromDate('');
    setToDate('');
    setDocType('all');
    setMobilePage(1);
    fetchInitialData();
  };

  const calculateDaysLeft = (dateString) => {
    if (!dateString || dateString === '—' || dateString === null || dateString === '') return null;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(dateString);
      if (isNaN(target.getTime())) return null;
      const diffTime = target - today;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch { return null; }
  };

  const handleFinalSearch = async () => {
    setLoading(true);
    try {
      setMobilePage(1);
      let results = [...data];
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        results = results.filter(item =>
          (item.vehicleNo && item.vehicleNo.toLowerCase().includes(query)) ||
          (item.ownerName && item.ownerName.toLowerCase().includes(query)) ||
          (item.party_name && item.party_name.toLowerCase().includes(query))
        );
      }
      if (fromDate && toDate) {
        results = results.filter(item => {
          if (docType === 'tax') return item.taxValidity && item.taxValidity >= fromDate && item.taxValidity <= toDate;
          if (docType === 'fitness') return item.fitnessValidity && item.fitnessValidity >= fromDate && item.fitnessValidity <= toDate;
          if (docType === 'insurance') return item.insuranceValidity && item.insuranceValidity >= fromDate && item.insuranceValidity <= toDate;
          const taxMatch = item.taxValidity && item.taxValidity >= fromDate && item.taxValidity <= toDate;
          const fitnessMatch = item.fitnessValidity && item.fitnessValidity >= fromDate && item.fitnessValidity <= toDate;
          const insuranceMatch = item.insuranceValidity && item.insuranceValidity >= fromDate && item.insuranceValidity <= toDate;
          return taxMatch || fitnessMatch || insuranceMatch;
        });
      }
      setData(results);
      if (results.length === 0) toast.info("No records found");
      else toast.success(`Found ${results.length} records`);
    } catch (error) { toast.error("Search failed!"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, vehicleNo) => {
    if (!id) { toast.error("Record ID missing!"); return; }
    if (window.confirm(`Delete ${vehicleNo}?`)) {
      const tId = toast.loading("Deleting...");
      try {
        await deleteInsuranceData(id);
        setData(prev => prev.filter(item => item.id !== id));
        toast.success("Deleted!", { id: tId });
      } catch (err) { toast.error("Delete failed!", { id: tId }); }
    }
  };

  const handleEditClick = (item) => {
    if (!item.id) { toast.error("Record ID missing!"); return; }
    setEditingItem({ ...item });
    setIsEditModalOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    const tId = toast.loading("Updating...");
    try {
      const response = await updateInsuranceData(editingItem.id, editingItem);
      setData(prev => prev.map(item => item.id === editingItem.id ? response.data : item));
      toast.success("Updated!", { id: tId });
      setIsEditModalOpen(false);
    } catch (error) { toast.error("Update failed!", { id: tId }); }
    finally { setUpdateLoading(false); }
  };

  // ---------- WhatsApp Send Function ----------
  // ========== REPLACE YOUR OLD sendWhatsAppMessage WITH THIS ==========
  const sendWhatsAppMessage = (item) => {
    // 1. Validate mobile number
    let mobile = item.mobile_no;
    if (!mobile) {
      toast.error("Mobile number not available for this record!");
      return;
    }
    let cleanNumber = mobile.toString().replace(/\D/g, '');
    if (cleanNumber.length === 10) cleanNumber = '91' + cleanNumber;
    else if (cleanNumber.length === 12 && cleanNumber.startsWith('91')) { /* ok */ }
    else if (cleanNumber.length === 13 && cleanNumber.startsWith('+91')) cleanNumber = cleanNumber.substring(1);
    else {
      toast.error("Invalid mobile number format");
      return;
    }

    // 2. Decide which document expiry is most urgent
    const docs = [
      { type: 'Tax', date: item.taxValidity },
      { type: 'Fitness', date: item.fitnessValidity },
      { type: 'Insurance', date: item.insuranceValidity }
    ].filter(d => d.date && d.date !== '—' && d.date !== null && d.date !== '');

    if (docs.length === 0) {
      toast.error("No expiry dates available for this vehicle!");
      return;
    }

    // Find the document with minimum days left (most urgent)
    let mostUrgent = docs[0];
    let minDays = calculateDaysLeft(mostUrgent.date);
    for (let i = 1; i < docs.length; i++) {
      const days = calculateDaysLeft(docs[i].date);
      if (days < minDays) {
        minDays = days;
        mostUrgent = docs[i];
      }
    }

    const docType = mostUrgent.type;
    const expiryDate = mostUrgent.date;

    // 3. Prepare the exact message template
    const ownerName = item.ownerName || item.party_name || 'Customer';
    const vehicleNo = item.vehicleNo || 'your vehicle';

    const message = `Hello ${ownerName},

Aapki gadi no ${vehicleNo} ka ${docType} expiry ${expiryDate} ko hone wala hai.

Kripya turant hamari agency se sampark kare:
📞 7389042205

THANK YOU 
- D.K. RTO & INSURENCE ADVISOR`;

    // 4. Open WhatsApp
    const encodedMsg = encodeURIComponent(message);
    const waLink = `https://wa.me/${cleanNumber}?text=${encodedMsg}`;
    window.open(waLink, '_blank');
  };
  // -----------------------------------------

  const handleExport = () => {
    if (data.length === 0) return toast.error("No data to export!");
    const exportData = data.map(item => ({
      'Vehicle No': item.vehicleNo || '-',
      'Owner Name': item.ownerName || item.party_name || '-',
      'Chassis No': item.chassisNumber || '-',
      'Mobile No': item.mobile_no || '-',
      'Tax Validity': item.taxValidity || '-',
      'Fitness Validity': item.fitnessValidity || '-',
      'Insurance Validity': item.insuranceValidity || '-',
      'Pollution Validity': item.pollutionValidity || '-',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ExpiryAnalysis");
    XLSX.writeFile(wb, `Expiry_Analysis_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Export successful!");
  };

  const getOverallStatus = (item) => {
    const taxDays = calculateDaysLeft(item.taxValidity);
    const fitnessDays = calculateDaysLeft(item.fitnessValidity);
    const insuranceDays = calculateDaysLeft(item.insuranceValidity);
    if ((taxDays !== null && taxDays < 0) || (fitnessDays !== null && fitnessDays < 0) || (insuranceDays !== null && insuranceDays < 0)) return 'Expired';
    if ((taxDays !== null && taxDays <= 30) || (fitnessDays !== null && fitnessDays <= 30) || (insuranceDays !== null && insuranceDays <= 30)) return 'Expiring Soon';
    return 'Valid';
  };

  const renderStatusCell = (date, docName) => {
    const days = calculateDaysLeft(date);
    if (!date || date === '—' || date === null || date === '') {
      return (
        <td data-label={docName === 'tax' ? 'Tax Expiry' : docName === 'fitness' ? 'Fitness Expiry' : 'Insurance Expiry'}>
          <div className="date-val">—</div>
          <span className="mini-tag gray">Not Set</span>
        </td>
      );
    }
    const isExpired = days < 0;
    const isSoon = days <= 30 && days >= 0;
    let statusClass = '', tagClass = '';
    if (isExpired) { statusClass = 'status-expired'; tagClass = 'red'; }
    else if (isSoon) { statusClass = 'status-warning'; tagClass = 'orange'; }
    else { statusClass = 'status-safe'; tagClass = 'green'; }
    let label = docName === 'tax' ? 'Tax Expiry' : docName === 'fitness' ? 'Fitness Expiry' : 'Insurance Expiry';
    return (
      <td data-label={label}>
        <div className={`date-val ${statusClass}`}>{date}</div>
        <span className={`mini-tag ${tagClass}`}>
          {isExpired ? `Expired ${Math.abs(days)} days ago` : `${days} days left`}
        </span>
      </td>
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleCard = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getStatusText = (date) => {
    const days = calculateDaysLeft(date);
    if (!date || days === null) return 'Not Set';
    if (days < 0) return `Expired ${Math.abs(days)} days ago`;
    if (days <= 30) return `${days} days left`;
    return 'Valid';
  };

  const getStatusClass = (date) => {
    const days = calculateDaysLeft(date);
    if (!date || days === null) return '';
    if (days < 0) return 'expired';
    if (days <= 30) return 'warning';
    return 'safe';
  };

  const renderMobileCard = (item) => {
    const isExpanded = expandedCards[item.id];
    return (
      <div key={item.id} className="mobile-card" onClick={() => toggleCard(item.id)}>
        <div className="mobile-card-header">
          <div className="vehicle-info">
            <div className="vehicle-number">{item.vehicleNo || '-'}</div>
            <div className="owner-name">{item.ownerName || item.party_name || '-'}</div>
          </div>
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
        <div className="mobile-card-sub">
          <span>📱 {item.mobile_no || '-'}</span>
          <span>📅 {item.createdDate || new Date().toLocaleDateString()}</span>
        </div>
        {isExpanded && (
          <div className="mobile-card-details">
            <div className="detail-row"><strong>Chassis:</strong> {item.chassisNumber || '-'}</div>
            {(docType === 'all' || docType === 'tax') && (
              <div className="detail-row"><strong>Tax:</strong> {item.taxValidity || '—'} <span className={`status-badge ${getStatusClass(item.taxValidity)}`}>{getStatusText(item.taxValidity)}</span></div>
            )}
            {(docType === 'all' || docType === 'fitness') && (
              <div className="detail-row"><strong>Fitness:</strong> {item.fitnessValidity || '—'} <span className={`status-badge ${getStatusClass(item.fitnessValidity)}`}>{getStatusText(item.fitnessValidity)}</span></div>
            )}
            {(docType === 'all' || docType === 'insurance') && (
              <div className="detail-row"><strong>Insurance:</strong> {item.insuranceValidity || '—'} <span className={`status-badge ${getStatusClass(item.insuranceValidity)}`}>{getStatusText(item.insuranceValidity)}</span></div>
            )}
            <div className="detail-row"><strong>Pollution:</strong> {item.pollutionValidity || '—'} <span className={`status-badge ${getStatusClass(item.pollutionValidity)}`}>{getStatusText(item.pollutionValidity)}</span></div>
            <div className="action-buttons">
              <button className="icon-btn send-i" onClick={(e) => { e.stopPropagation(); sendWhatsAppMessage(item); }}><Send size={14} /></button>
              <button className="icon-btn del-i" onClick={(e) => { e.stopPropagation(); handleDelete(item.id, item.vehicleNo); }}><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="analysis-wrapper">
      <Toaster position="top-center" />

      {/* FILTER PANEL */}
      <div className="filter-panel no-print">
        <div className="input-group">
          <label><Search size={12} /> Search</label>
          <input type="text" placeholder="Vehicle / Owner..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleFinalSearch()} />
        </div>
        <div className="input-group">
          <label><FileText size={12} /> Filter Type</label>
          <select className="custom-select" value={docType} onChange={(e) => setDocType(e.target.value)}>
            <option value="all">All Documents</option>
            <option value="tax">Tax</option>
            <option value="fitness">Fitness</option>
            <option value="insurance">Insurance</option>
          </select>
        </div>
        <div className="input-group">
          <label><Calendar size={12} /> From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="input-group">
          <label><Calendar size={12} /> To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="action-row">
          <button className="btn-go" onClick={handleFinalSearch}>{loading ? <Loader2 className="spinner" size={14} /> : <Filter size={14} />} Apply</button>
          <button className="btn-refresh" onClick={handleRefresh}><RefreshCw size={14} className={loading ? "spinner" : ""} /></button>
          <button className="btn-xl" onClick={handleExport}><FileSpreadsheet size={16} /></button>
          <button className="btn-xl" onClick={handlePrint}><Printer size={16} /></button>
        </div>
      </div>

      {/* DESKTOP TABLE */}
      {!isMobile && (
        <div className="scroll-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Vehicle / Owner</th>
                <th>Chassis No</th>
                {(docType === 'all' || docType === 'tax') && <th>Tax Expiry</th>}
                {(docType === 'all' || docType === 'fitness') && <th>Fitness Expiry</th>}
                {(docType === 'all' || docType === 'insurance') && <th>Insurance Expiry</th>}
                <th className="no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? data.map((item) => (
                <tr key={item.id}>
                  <td data-label="Vehicle / Owner">
                    <div className="v-box">
                      <span className="v-num">{item.vehicleNo || '-'}</span>
                      <span className="o-nam">{item.ownerName || item.party_name || '-'}</span>
                    </div>
                  </td>
                  <td data-label="Chassis No">
                    <span className="v-num">{item.chassisNumber || '-'}</span>
                  </td>
                  {(docType === 'all' || docType === 'tax') && renderStatusCell(item.taxValidity, 'tax')}
                  {(docType === 'all' || docType === 'fitness') && renderStatusCell(item.fitnessValidity, 'fitness')}
                  {(docType === 'all' || docType === 'insurance') && renderStatusCell(item.insuranceValidity, 'insurance')}
                  <td data-label="Actions" className="no-print">
                    <div className="row-btns">
                      <button className="icon-btn send-i" onClick={() => sendWhatsAppMessage(item)}><Send size={14} /></button>
                      <button className="icon-btn del-i" onClick={() => handleDelete(item.id, item.vehicleNo)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={docType === 'all' ? 6 : 5} className="empty-state">{loading ? <Loader2 className="spinner" size={40} /> : "No records found."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* MOBILE CARDS */}
      {isMobile && (
        <div className="mobile-cards-container">
          {loading ? (
            <div className="loading-spinner">
              <Loader2 className="spinner" size={40} />
            </div>
          ) : data.length === 0 ? (
            <div className="empty-state">No records found.</div>
          ) : (
            <>
              {data
                .slice(
                  (mobilePage - 1) * mobilePageSize,
                  mobilePage * mobilePageSize
                )
                .map(item => renderMobileCard(item))}

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "15px",
                  marginBottom: "20px"
                }}
              >
                <Pagination
                  current={mobilePage}
                  pageSize={mobilePageSize}
                  total={data.length}
                  onChange={(page) => setMobilePage(page)}
                  showSizeChanger={false}
                  size="small"
                />
              </div>
            </>
          )}
        </div>
      )}
      {/* EDIT MODAL */}
      {isEditModalOpen && editingItem && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <span style={{ fontWeight: 'bold' }}>Edit: {editingItem.vehicleNo}</span>
              <X size={20} style={{ cursor: 'pointer' }} onClick={() => setIsEditModalOpen(false)} />
            </div>
            <form onSubmit={handleUpdate}>
              <div className="modal-body">
                <div className="form-field"><label>Owner Name</label><input type="text" value={editingItem.ownerName || editingItem.party_name || ''} onChange={e => setEditingItem({ ...editingItem, ownerName: e.target.value, party_name: e.target.value })} required /></div>
                <div className="form-field"><label>Vehicle Number</label><input type="text" value={editingItem.vehicleNo || ''} onChange={e => setEditingItem({ ...editingItem, vehicleNo: e.target.value })} required /></div>
                <div className="form-field"><label>Chassis Number</label><input type="text" value={editingItem.chassisNumber || ''} onChange={e => setEditingItem({ ...editingItem, chassisNumber: e.target.value })} /></div>
                <div className="form-field"><label>Mobile Number</label><input type="text" value={editingItem.mobile_no || ''} onChange={e => setEditingItem({ ...editingItem, mobile_no: e.target.value })} /></div>
                <div className="form-field"><label>Tax Validity</label><input type="date" value={editingItem.taxValidity || ''} onChange={e => setEditingItem({ ...editingItem, taxValidity: e.target.value })} /></div>
                <div className="form-field"><label>Fitness Validity</label><input type="date" value={editingItem.fitnessValidity || ''} onChange={e => setEditingItem({ ...editingItem, fitnessValidity: e.target.value })} /></div>
                <div className="form-field"><label>Insurance Validity</label><input type="date" value={editingItem.insuranceValidity || ''} onChange={e => setEditingItem({ ...editingItem, insuranceValidity: e.target.value })} /></div>
                <div className="form-field"><label>Pollution Validity</label><input type="date" value={editingItem.pollutionValidity || ''} onChange={e => setEditingItem({ ...editingItem, pollutionValidity: e.target.value })} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-update" disabled={updateLoading}>{updateLoading ? <Loader2 className="spinner" size={18} /> : <Save size={18} />} Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpiryAnalysis;